import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveOwnBoxes } from '../../hooks/useActiveOwnBoxes';
import { RateItem, RateItemFormRef } from '../../models/rate.types';
import {
  buildPackagingRateItem,
  getCurrentPackagingRateItem,
  mergePackagingPrices,
} from '../../utils/packagingRateItem';
import { PackagingPricesTable } from '../interaction/PackagingPricesTable';

interface AddPackagingItemFormProps {
  initialData?: RateItem;
  existingItems?: RateItem[];
}

export const AddPackagingItemForm = forwardRef<RateItemFormRef, AddPackagingItemFormProps>(
  ({ initialData, existingItems = [] }, ref) => {
    const { t } = useTranslation('ratesModule');
    const { ownBoxes, isLoading } = useActiveOwnBoxes();
    const [prices, setPrices] = useState<Record<number, number>>({});
    const [errors, setErrors] = useState<Record<number, string>>({});

    const initializePrices = useCallback(() => {
      const sourceItem = initialData?.type === 'packaging' ? initialData : getCurrentPackagingRateItem(existingItems);
      setPrices(mergePackagingPrices(ownBoxes, sourceItem));
      setErrors({});
    }, [initialData, existingItems, ownBoxes]);

    useEffect(() => {
      if (ownBoxes.length > 0) {
        initializePrices();
      }
    }, [ownBoxes, initializePrices]);

    const handlePriceChange = useCallback((packagingId: number, value: string) => {
      const parsed = value.trim() === '' ? NaN : parseFloat(value);
      setPrices((prev) => ({
        ...prev,
        [packagingId]: Number.isNaN(parsed) ? 0 : parsed,
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[packagingId];
        return next;
      });
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getData: async () => {
          if (ownBoxes.length === 0) {
            return null;
          }

          const priceErrors: Record<number, string> = {};
          for (const box of ownBoxes) {
            const price = prices[box.id];
            if (price === undefined || price < 0 || Number.isNaN(price)) {
              priceErrors[box.id] = t('validation.feePositive');
            }
          }

          if (Object.keys(priceErrors).length > 0) {
            setErrors(priceErrors);
            return null;
          }

          return buildPackagingRateItem(ownBoxes, prices);
        },
      }),
      [ownBoxes, prices, t],
    );

    if (isLoading) {
      return <p className="text-sm text-subtext">{t('sections.packagingPrices.loading')}</p>;
    }

    return (
      <div className="space-y-3">
        <p className="text-xs text-subtext">{t('sections.packagingPrices.helper')}</p>
        <PackagingPricesTable ownBoxes={ownBoxes} prices={prices} onPriceChange={handlePriceChange} errors={errors} />
      </div>
    );
  },
);

AddPackagingItemForm.displayName = 'AddPackagingItemForm';
